import {
  AI_CHAT_MAX_CONTENT,
  PermissionCode,
  type AiChatMessage,
  type KbAiProposal,
} from '@gb-ticket/shared-types';
import { SendIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AiLiveReply } from '@/platform/components/ai-live-reply';
import { AiMarkdown } from '@/platform/components/ai-markdown';
import { KbProposalCard } from '@/products/helpdesk/features/kb/kb-proposal-card';
import { useKbArticles } from '@/products/helpdesk/features/kb/use-kb';
import {
  useApplyKbProposal,
  useKbAssistantChat,
  useRejectKbProposal,
} from '@/products/helpdesk/features/kb/use-kb-ai';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type Line = { role: 'user' | 'assistant'; content: string };

const NONE = 'none';

export function KbAssistantPage() {
  const me = useMe();
  const copy = useVoiceCopy();
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const [params] = useSearchParams();
  const articles = useKbArticles(
    { page: 1, pageSize: 100, sortBy: 'title', sortOrder: 'asc' },
    { enabled: canWrite },
  );
  const chat = useKbAssistantChat();
  const apply = useApplyKbProposal();
  const reject = useRejectKbProposal();
  const [articleSlug, setArticleSlug] = useState(params.get('artikel') ?? NONE);
  const [input, setInput] = useState('');
  const [lines, setLines] = useState<Line[]>([
    {
      role: 'assistant',
      content: copy('helpdesk.kb.assistant.intro'),
    },
  ]);
  const [proposal, setProposal] = useState<KbAiProposal | null>(null);

  const selectedTitle = useMemo(
    () => articles.data?.items.find((item) => item.slug === articleSlug)?.title,
    [articles.data?.items, articleSlug],
  );

  if (!canWrite) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">Den KB-Assistenten können nur Bearbeiter nutzen.</p>
      </section>
    );
  }

  async function send() {
    const text = input.trim();
    if (!text || chat.isPending) {
      return;
    }
    const next: Line[] = [...lines, { role: 'user', content: text }];
    setLines(next);
    setInput('');
    const payload: AiChatMessage[] = next
      .slice(1)
      .map((line) => ({ role: line.role, content: line.content }));
    try {
      const result = await chat.mutateAsync({
        messages: payload,
        articleSlug: articleSlug === NONE ? undefined : articleSlug,
      });
      setLines((current) => [...current, { role: 'assistant', content: result.answer }]);
      setProposal(result.proposal);
    } catch (error) {
      setLines((current) => [
        ...current,
        {
          role: 'assistant',
          content:
            error instanceof ApiRequestError
              ? error.message
              : 'Der Assistent ist gerade nicht erreichbar.',
        },
      ]);
    }
  }

  async function onApply() {
    if (!proposal) {
      return;
    }
    try {
      const saved = await apply.mutateAsync(proposal.id);
      toast.success(
        saved.kind === 'ARTICLE_DRAFT'
          ? 'Entwurf angelegt. Bitte prüfen und erst dann veröffentlichen.'
          : 'Neue Artikelversion gespeichert.',
      );
      setProposal(saved);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Übernehmen fehlgeschlagen.');
    }
  }

  async function onReject() {
    if (!proposal) {
      return;
    }
    try {
      await reject.mutateAsync(proposal.id);
      toast.success('Vorschlag verworfen.');
      setProposal(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verwerfen fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/helpdesk/wissen">Alle Artikel</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">KB-Assistent</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Änderungen in natürlicher Sprache. Jeder Vorschlag braucht Ihre Freigabe.
            {selectedTitle ? ` Aktuell: ${selectedTitle}.` : null}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/helpdesk/wissen/optimierung">KB-Optimierung</Link>
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex h-[min(36rem,70vh)] flex-col overflow-hidden rounded-xl border">
          <div className="border-b p-3">
            <Label htmlFor="kb-assistant-article">Artikel (optional)</Label>
            <Select value={articleSlug} onValueChange={setArticleSlug}>
              <SelectTrigger id="kb-assistant-article" className="mt-1">
                <SelectValue placeholder="Artikel wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Automatisch erkennen</SelectItem>
                {(articles.data?.items ?? []).map((item) => (
                  <SelectItem key={item.id} value={item.slug}>
                    {item.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            {lines.map((line, index) => (
              <article
                key={`${line.role}-${index}`}
                className={cn(
                  'max-w-[90%] rounded-lg px-3 py-2 text-sm',
                  line.role === 'user' ? 'ml-auto bg-primary text-primary-foreground' : 'bg-muted',
                )}
              >
                {line.role === 'assistant' ? <AiMarkdown text={line.content} /> : line.content}
              </article>
            ))}
            {chat.isPending ? <AiLiveReply active text="" /> : null}
          </div>
          <form
            className="flex gap-2 border-t p-3"
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            <label className="sr-only" htmlFor="kb-assistant-input">
              Änderung diktieren
            </label>
            <textarea
              id="kb-assistant-input"
              rows={2}
              maxLength={AI_CHAT_MAX_CONTENT}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ergänze im Artikel … einen Hinweis auf …"
              className="min-h-12 flex-1 resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
            <Button
              type="submit"
              size="icon"
              disabled={chat.isPending || input.trim().length === 0}
              aria-label="Senden"
            >
              <SendIcon />
            </Button>
          </form>
        </div>
        <div className="space-y-3">
          {proposal?.status === 'PENDING' ? (
            <KbProposalCard
              proposal={proposal}
              onApply={() => void onApply()}
              onReject={() => void onReject()}
              busy={apply.isPending || reject.isPending}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Hier erscheint das Diff, sobald ein Vorschlag vorliegt. Übernehmen legt eine neue
              Version bzw. einen Entwurf an, veröffentlicht aber nichts von allein.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
